import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useLocation, useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import {
    getMetaApps,
    createMetaApp,
    updateMetaApp,
    updateMetaAppStatus,
    deleteMetaApp,
    getMetaUsers,
    revokeMetaUser,
    initMetaOAuth,
    updateMetaPageStatus,
    updateMetaPageConfig,
    getPageCategories,
} from '@/services/api';
import {
    getMetaPages,
    deleteMetaPage,
    updateMetaUserProject,
    updateMetaPageProject,
} from '@/services/api/connections';
import {
    getPageGroups,
    createPageGroup,
    updatePageGroup,
    deletePageGroup,
} from '@/services/api/page-groups';
import { getProjects } from '@/services/api/projects';
import {
    MetaApp,
    MetaUser,
    MetaPage,
    MetaConnectionTab,
    MetaAppFormData,
    CreateMetaAppInput,
    UpdatePageConfigInput,
    PageGroup,
    AppCapability,
    CreatePageGroupInput,
} from './types/meta.types';

// Components
import PagesList from './components/PagesList';
import CategoriesList from './components/CategoriesList';
import AccountsList from './components/AccountsList';
import SyncPagesDialog from './components/SyncPagesDialog';
import AppsList from './components/AppsList';
import PageGroupsList from './components/PageGroupsList';
import CreateEditPageGroupDialog from './components/CreateEditPageGroupDialog';
import CollaboratorsList from './components/CollaboratorsList';
import EditRateLimitDialog from './components/EditRateLimitDialog';
import CreateEditAppDialog from './components/CreateEditAppDialog';
import EditAppProjectsDialog from './components/EditAppProjectsDialog';
import ConnectAccountDialog from './components/ConnectAccountDialog';
import DeleteAppDialog from './components/DeleteAppDialog';
import { getCurrentUser } from '@/services/api/auth';
import ConfigurePageDialog from './components/ConfigurePageDialog';
import WebhookConfigDialog from './components/WebhookConfigDialog';
import { SyncLeadsDialog, FloatingSyncStatus } from '../Leads/components';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';

export default function Connection() {
    // current_user: usado pra decidir quais apps o user pode gerenciar (delete/edit/toggle).
    // - super_admin / owner → pode tudo
    // - User normal → só apps cujo project_id está em user.projects
    // - Apps públicos (project_id NULL) → só admin
    const currentUser = useMemo(() => getCurrentUser(), []);
    const isSuperAdmin = useMemo(() => {
        if (!currentUser) return false;
        if (currentUser.owner) return true;
        const utype = (currentUser.user_type || '').toLowerCase();
        if (utype === 'super_admin' || utype === 'admin') return true;
        // `role: 'admin'` cobre o usuário admin original do conectablog.
        // NOTA: `permissions: ["*"]` NÃO conta — partners também recebem
        // esse wildcard mas não devem ver apps de outros projetos.
        if ((currentUser.role || '').toLowerCase() === 'admin') return true;
        return false;
    }, [currentUser]);
    const userProjectIds = useMemo(() => {
        const ids = currentUser?.projects || [];
        // Pode vir como list<number> ou list<{id}>. Normaliza.
        const s = new Set<number>();
        for (const p of ids) {
            if (typeof p === 'number') s.add(p);
            else if (p && typeof p === 'object' && 'id' in (p as any)) s.add(Number((p as any).id));
        }
        return s;
    }, [currentUser]);
    const canManageApp = useMemo(() => {
        return (app: MetaApp) => {
            if (isSuperAdmin) return true;
            // Multi-project: user pode gerenciar se tem acesso a QUALQUER um dos projects do app.
            // Fallback legacy: project_id quando project_ids está vazio.
            const projectIds = (app.project_ids && app.project_ids.length > 0)
                ? app.project_ids
                : (app.project_id ? [app.project_id] : []);
            if (projectIds.length === 0) return false; // app público só admin
            return projectIds.some((pid) => userProjectIds.has(Number(pid)));
        };
    }, [isSuperAdmin, userProjectIds]);

    const [activeTab, setActiveTab] = useState<MetaConnectionTab>('pages');
    const [isCreateAppDialogOpen, setIsCreateAppDialogOpen] = useState(false);
    const [isEditAppDialogOpen, setIsEditAppDialogOpen] = useState(false);
    // Modal compacto pra editar só os projetos vinculados (sem form completo)
    const [editAppProjectsTarget, setEditAppProjectsTarget] = useState<MetaApp | null>(null);
    const [isConnectDialogOpen, setIsConnectDialogOpen] = useState(false);
    const [isDeleteAppDialogOpen, setIsDeleteAppDialogOpen] = useState(false);
    const [selectedApp, setSelectedApp] = useState<MetaApp | null>(null);
    const [appToDelete, setAppToDelete] = useState<MetaApp | null>(null);
    const [authToRevoke, setAuthToRevoke] = useState<MetaUser | null>(null);
    const [syncAuth, setSyncAuth] = useState<MetaUser | null>(null);
    const [pageToConfigure, setPageToConfigure] = useState<MetaPage | null>(null);
    const [isConfigurePageDialogOpen, setIsConfigurePageDialogOpen] = useState(false);
    const [isSyncLeadsDialogOpen, setIsSyncLeadsDialogOpen] = useState(false);
    const [pageToSync, setPageToSync] = useState<MetaPage | null>(null);
    const [pageToDelete, setPageToDelete] = useState<MetaPage | null>(null);
    const [activeSyncTaskId, setActiveSyncTaskId] = useState<string | null>(null);
    const [selectedAppIdForConnect, setSelectedAppIdForConnect] = useState<number | null>(null);
    const [webhookConfigApp, setWebhookConfigApp] = useState<MetaApp | null>(null);
    const [isWebhookConfigDialogOpen, setIsWebhookConfigDialogOpen] = useState(false);
    const [rateLimitApp, setRateLimitApp] = useState<MetaApp | null>(null);
    const [isRateLimitDialogOpen, setIsRateLimitDialogOpen] = useState(false);
    // URL de callback é fixa (derivada da API) — não é editável pelo user.
    const META_API_BASE = (import.meta.env.VITE_META_API_URL || '').replace(/\/$/, '');
    const FIXED_REDIRECT_URI = META_API_BASE ? `${META_API_BASE}/meta/oauth/callback` : '';

    const [appFormData, setAppFormData] = useState<MetaAppFormData>({
        app_name: '',
        app_id: '',
        app_secret: '',
        redirect_uri: FIXED_REDIRECT_URI,
        api_version: 'v18.0',
        is_private: false,
        project_id: null,
        project_ids: [],
        meta_mode: 'production',
        // Default pra production: todas as capabilities marcadas.
        // User desmarca o que o app não tem permission no Meta.
        // Switch pra meta_mode='dev' precisaria recortar pra ['broadcast', 'flow'],
        // mas como o input default é 'production' aqui, mantemos as 4.
        capabilities: ['broadcast', 'flow', 'lead_profile', 'comment_moderation'],
    });

    // Page Groups state
    const [isPageGroupDialogOpen, setIsPageGroupDialogOpen] = useState(false);
    const [editingPageGroup, setEditingPageGroup] = useState<PageGroup | null>(null);
    const [pageGroupToDelete, setPageGroupToDelete] = useState<PageGroup | null>(null);

    const { toast } = useToast();
    const queryClient = useQueryClient();
    const location = useLocation();
    const navigate = useNavigate();

    // Handle OAuth callback result
    useEffect(() => {
        const state = location.state as { oauthSuccess?: boolean; oauthError?: string; needsTesterAccess?: boolean } | null;
        if (!state) return;

        if (state.oauthSuccess) {
            if (state.needsTesterAccess) {
                toast({
                    title: '⚠️ Nenhuma página encontrada',
                    description:
                        'O app está em modo de desenvolvimento. Peça ao administrador para adicionar ' +
                        'sua conta como Tester em developers.facebook.com e tente sincronizar novamente.',
                });
            } else {
                toast({
                    title: 'Conta conectada!',
                    description: 'A autenticação com o Meta foi realizada com sucesso.',
                });
            }
            queryClient.invalidateQueries({ queryKey: ['meta-users'] });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
        } else if (state.oauthError) {
            toast({
                variant: 'destructive',
                title: 'Erro na autenticação',
                description: state.oauthError,
            });
        }

        // Clear navigation state to prevent re-triggering on refresh
        navigate(location.pathname, { replace: true, state: null });
    }, [location.state]);



    // Queries
    const { data: apps = [], isLoading: isLoadingApps } = useQuery({
        queryKey: ['meta-apps'],
        queryFn: getMetaApps,
    });

    const { data: authentications = [], isLoading: isLoadingAuths } = useQuery({
        queryKey: ['meta-users'],
        queryFn: getMetaUsers,
    });

    const { data: pages = [], isLoading: isLoadingPages } = useQuery({
        queryKey: ['meta-pages'],
        queryFn: getMetaPages,
    });

    const { data: categories = [] } = useQuery({
        queryKey: ['page-categories'],
        queryFn: getPageCategories,
    });

    const { data: pageGroups = [] } = useQuery({
        queryKey: ['page-groups'],
        queryFn: getPageGroups,
    });

    // Projetos do apisistema — usados pra vincular apps. O backend já filtra
    // pelos projetos que o user atual tem acesso (via user.projects[]).
    const { data: projects = [] } = useQuery({
        queryKey: ['projects'],
        queryFn: getProjects,
        staleTime: 1000 * 60 * 5,  // 5min — projetos não mudam muito
    });

    const projectOptions = useMemo(
        () => projects.map(p => ({ id: p.id, name: p.name })),
        [projects],
    );
    const projectNameById = useMemo(() => {
        const m = new Map<number, string>();
        projects.forEach(p => m.set(p.id, p.name));
        return m;
    }, [projects]);

    // Mutations - Page Groups
    const createPageGroupMutation = useMutation({
        mutationFn: createPageGroup,
        onSuccess: () => {
            toast({ title: 'Grupo criado!', description: 'O grupo de páginas foi criado com sucesso.' });
            queryClient.invalidateQueries({ queryKey: ['page-groups'] });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
            setIsPageGroupDialogOpen(false);
            setEditingPageGroup(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar grupo',
                description: error.response?.data?.detail || 'Não foi possível criar o grupo.',
            });
        },
    });

    const updatePageGroupMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: CreatePageGroupInput }) => updatePageGroup(id, data),
        onSuccess: () => {
            toast({ title: 'Grupo atualizado!' });
            queryClient.invalidateQueries({ queryKey: ['page-groups'] });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
            setIsPageGroupDialogOpen(false);
            setEditingPageGroup(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar grupo',
                description: error.response?.data?.detail || 'Não foi possível atualizar o grupo.',
            });
        },
    });

    const deletePageGroupMutation = useMutation({
        mutationFn: deletePageGroup,
        onSuccess: () => {
            toast({ title: 'Grupo excluído!' });
            queryClient.invalidateQueries({ queryKey: ['page-groups'] });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
            setPageGroupToDelete(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao excluir grupo',
                description: error.response?.data?.detail || 'Não foi possível excluir o grupo.',
            });
        },
    });

    const handleSubmitPageGroup = (data: CreatePageGroupInput) => {
        if (editingPageGroup) {
            updatePageGroupMutation.mutate({ id: editingPageGroup.id, data });
        } else {
            createPageGroupMutation.mutate(data);
        }
    };

    const handleOpenEditPageGroup = (group: PageGroup) => {
        setEditingPageGroup(group);
        setIsPageGroupDialogOpen(true);
    };

    const handleOpenCreatePageGroup = () => {
        setEditingPageGroup(null);
        setIsPageGroupDialogOpen(true);
    };

    // Mutations - Apps
    const createAppMutation = useMutation({
        mutationFn: createMetaApp,
        onSuccess: (createdApp) => {
            toast({
                title: 'App criada!',
                description: 'A aplicação foi criada com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-apps'] });
            handleCloseCreateAppDialog();
            // Abre automaticamente o diálogo de configuração do webhook —
            // o usuário precisa do verify_token para configurar no Meta.
            setWebhookConfigApp(createdApp);
            setIsWebhookConfigDialogOpen(true);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao criar app',
                description: error.response?.data?.detail || 'Ocorreu um erro ao criar a app.',
            });
        },
    });

    const updateAppMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: any }) => updateMetaApp(id, data),
        onSuccess: () => {
            toast({
                title: 'App atualizada!',
                description: 'A aplicação foi atualizada com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-apps'] });
            handleCloseEditAppDialog();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar app',
                description: error.response?.data?.detail || 'Ocorreu um erro ao atualizar a app.',
            });
        },
    });

    const deleteAppMutation = useMutation({
        mutationFn: deleteMetaApp,
        onSuccess: () => {
            toast({
                title: 'App excluída!',
                description: 'A aplicação foi excluída com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-apps'] });
            handleCloseDeleteAppDialog();
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao excluir app',
                description: error.response?.data?.detail || 'Ocorreu um erro ao excluir a app.',
            });
        },
    });

    const toggleAppStatusMutation = useMutation({
        mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
            updateMetaAppStatus(id, is_active),
        onSuccess: () => {
            toast({
                title: 'Status atualizado!',
                description: 'O status da app foi atualizado com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-apps'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar status',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation - OAuth
    const initOAuthMutation = useMutation({
        mutationFn: initMetaOAuth,
        onSuccess: (data) => {
            // Redireciona para autorização do Facebook
            window.location.href = data.authorization_url;
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao iniciar OAuth',
                description: error.response?.data?.detail || 'Ocorreu um erro ao conectar.',
            });
        },
    });

    // Mutation - Revoke Authentication
    const revokeAuthMutation = useMutation({
        mutationFn: revokeMetaUser,
        onSuccess: () => {
            toast({
                title: 'Perfil removido!',
                description: 'A autenticação foi revogada com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-users'] });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao remover perfil',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation - Toggle Page Status
    const togglePageStatusMutation = useMutation({
        mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
            updateMetaPageStatus(id, is_active),
        onSuccess: () => {
            toast({
                title: 'Status atualizado!',
                description: 'O status da página foi atualizado com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar status',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation - Vincula a CONTA Meta a um projeto (default das pages dessa conta)
    const updateAuthProjectMutation = useMutation({
        mutationFn: ({ authId, projectId }: { authId: number; projectId: number | null }) =>
            updateMetaUserProject(authId, projectId),
        onSuccess: () => {
            toast({ title: 'Projeto da conta atualizado.' });
            queryClient.invalidateQueries({ queryKey: ['meta-users'] });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar projeto da conta',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation - Override do projeto pra uma PAGE específica (sobrescreve default da conta)
    const updatePageProjectMutation = useMutation({
        mutationFn: ({ pageId, projectId }: { pageId: number; projectId: number | null }) =>
            updateMetaPageProject(pageId, projectId),
        onSuccess: () => {
            toast({ title: 'Projeto da página atualizado.' });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao atualizar projeto da página',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation - Update Page Config
    const updatePageConfigMutation = useMutation({
        mutationFn: ({ id, data }: { id: number; data: UpdatePageConfigInput }) =>
            updateMetaPageConfig(id, data),
        onSuccess: () => {
            toast({
                title: 'Configuração salva!',
                description: 'As configurações da página foram atualizadas com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
            setIsConfigurePageDialogOpen(false);
            setPageToConfigure(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao salvar configuração',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Mutation - Delete Page
    const deletePageMutation = useMutation({
        mutationFn: (id: number) => deleteMetaPage(id),
        onSuccess: () => {
            toast({
                title: 'Página removida!',
                description: 'A página foi removida com sucesso.',
            });
            queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
            setPageToDelete(null);
        },
        onError: (error: any) => {
            toast({
                variant: 'destructive',
                title: 'Erro ao remover página',
                description: error.response?.data?.detail || 'Ocorreu um erro.',
            });
        },
    });

    // Handlers - Apps
    const handleCreateAppSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        const appData: CreateMetaAppInput = {
            app_name: appFormData.app_name,
            app_id: appFormData.app_id,
            app_secret: appFormData.app_secret,
            redirect_uri: FIXED_REDIRECT_URI,
            api_version: appFormData.api_version || 'v18.0',
            // Multi-project: [] = público, [X,Y] = privado vinculado aos X,Y
            project_ids: appFormData.is_private ? (appFormData.project_ids ?? []) : [],
            meta_mode: appFormData.meta_mode,
            capabilities: appFormData.capabilities ?? [],
        };

        createAppMutation.mutate(appData);
    };

    const handleEditAppSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (!selectedApp) return;

        updateAppMutation.mutate({
            id: selectedApp.id,
            data: {
                app_name: appFormData.app_name,
                app_secret: appFormData.app_secret,
                redirect_uri: FIXED_REDIRECT_URI,
                api_version: appFormData.api_version,
                project_ids: appFormData.is_private ? (appFormData.project_ids ?? []) : [],
                meta_mode: appFormData.meta_mode,
                capabilities: appFormData.capabilities ?? [],
            },
        });
    };

    /** Abre modal compacto pra editar SÓ os projects vinculados ao app. */
    const handleOpenEditAppProjects = (app: MetaApp) => {
        setEditAppProjectsTarget(app);
    };

    /** Salva projects via updateApp (apenas project_ids). */
    const handleSaveAppProjects = async (appId: number, projectIds: number[]) => {
        await new Promise<void>((resolve, reject) => {
            updateAppMutation.mutate(
                { id: appId, data: { project_ids: projectIds } },
                { onSuccess: () => resolve(), onError: (e) => reject(e) },
            );
        });
        setEditAppProjectsTarget(null);
    };

    const handleOpenEditAppDialog = (app: MetaApp) => {
        // Carrega project_ids (multi) com fallback legacy
        const pids = (app.project_ids && app.project_ids.length > 0)
            ? app.project_ids
            : (app.project_id ? [app.project_id] : []);
        setSelectedApp(app);
        // Capabilities: se vier null do backend (legacy), aplica default por
        // meta_mode (mesma lógica do backend). Assim o user vê os toggles
        // pré-marcados de forma consistente.
        const defaultCaps: AppCapability[] = app.meta_mode === 'dev'
            ? ['broadcast', 'flow']
            : ['broadcast', 'flow', 'lead_profile', 'comment_moderation'];
        setAppFormData({
            app_name: app.app_name,
            app_id: app.app_id,
            app_secret: '',
            redirect_uri: FIXED_REDIRECT_URI,
            api_version: app.api_version,
            is_private: pids.length > 0,
            project_id: pids[0] ?? null,
            project_ids: pids,
            meta_mode: app.meta_mode ?? 'production',
            capabilities: app.capabilities ?? defaultCaps,
        });
        setIsEditAppDialogOpen(true);
    };

    const handleOpenDeleteAppDialog = (app: MetaApp) => {
        setAppToDelete(app);
        setIsDeleteAppDialogOpen(true);
    };

    const handleConfirmDeleteApp = () => {
        if (appToDelete) {
            deleteAppMutation.mutate(appToDelete.id);
        }
    };

    const handleOpenWebhookConfig = (app: MetaApp) => {
        setWebhookConfigApp(app);
        setIsWebhookConfigDialogOpen(true);
    };

    const handleOpenRateLimitDialog = (app: MetaApp) => {
        setRateLimitApp(app);
        setIsRateLimitDialogOpen(true);
    };

    const handleSaveRateLimit = (rps: number) => {
        if (!rateLimitApp) return;
        updateAppMutation.mutate(
            { id: rateLimitApp.id, data: { requests_per_second: rps } },
            {
                onSuccess: () => {
                    setIsRateLimitDialogOpen(false);
                    setRateLimitApp(null);
                },
            }
        );
    };

    const handleToggleAppStatus = (app: MetaApp) => {
        if (app.is_default) {
            toast({
                variant: 'destructive',
                title: 'Ação não permitida',
                description: 'Não é possível desativar a app padrão do sistema.',
            });
            return;
        }

        toggleAppStatusMutation.mutate({
            id: app.id,
            is_active: !app.is_active,
        });
    };

    const handleCloseCreateAppDialog = () => {
        setIsCreateAppDialogOpen(false);
        setAppFormData({
            app_name: '',
            app_id: '',
            app_secret: '',
            redirect_uri: FIXED_REDIRECT_URI,
            api_version: 'v18.0',
            is_private: false,
            project_id: null,
            project_ids: [],
            meta_mode: 'production',
            capabilities: ['broadcast', 'flow', 'lead_profile', 'comment_moderation'],
        });
    };

    const handleCloseEditAppDialog = () => {
        setIsEditAppDialogOpen(false);
        setSelectedApp(null);
        setAppFormData({
            app_name: '',
            app_id: '',
            app_secret: '',
            redirect_uri: FIXED_REDIRECT_URI,
            api_version: 'v18.0',
            is_private: false,
            project_id: null,
            project_ids: [],
            meta_mode: 'production',
            capabilities: ['broadcast', 'flow', 'lead_profile', 'comment_moderation'],
        });
    };

    const handleCloseDeleteAppDialog = () => {
        setIsDeleteAppDialogOpen(false);
        setAppToDelete(null);
    };

    // Handlers - OAuth
    const handleOpenConnectDialog = () => {
        setSelectedAppIdForConnect(null);
        setIsConnectDialogOpen(true);
    };

    const handleConnect = () => {
        initOAuthMutation.mutate(selectedAppIdForConnect);
    };

    // Handlers - Auth
    const handleSyncPages = (auth: MetaUser) => {
        // Abre o picker: escolhe quais páginas dessa conta vincular (sem re-login).
        setSyncAuth(auth);
    };

    const handleRevokeAuth = (auth: MetaUser) => {
        setAuthToRevoke(auth);
    };

    const handleConfirmRevoke = () => {
        if (authToRevoke) {
            revokeAuthMutation.mutate(authToRevoke.id, {
                onSettled: () => setAuthToRevoke(null),
            });
        }
    };

    const handleTogglePageStatus = (page: MetaPage) => {
        togglePageStatusMutation.mutate({
            id: page.id,
            is_active: !page.is_active,
        });
    };

    const handleOpenConfigurePage = (page: MetaPage) => {
        setPageToConfigure(page);
        setIsConfigurePageDialogOpen(true);
    };

    const handleSavePageConfig = (pageId: number, data: UpdatePageConfigInput) => {
        updatePageConfigMutation.mutate({ id: pageId, data });
    };

    const handleOpenSyncLeads = (page: MetaPage) => {
        setPageToSync(page);
        setIsSyncLeadsDialogOpen(true);
    };

    const handleSyncLeadsComplete = () => {
        queryClient.invalidateQueries({ queryKey: ['meta-pages'] });
    };

    const handleDeletePage = (page: MetaPage) => {
        setPageToDelete(page);
    };

    const handleConfirmDeletePage = () => {
        if (pageToDelete) {
            deletePageMutation.mutate(pageToDelete.id);
        }
    };

    const handleBackgroundSync = (taskId: string) => {
        setActiveSyncTaskId(taskId);
        setIsSyncLeadsDialogOpen(false);
    };

    const isLoading = isLoadingApps || isLoadingAuths || isLoadingPages;

    return (
        <div className="space-y-6">
            {/* Dark Gradient Header — padrão das outras páginas Meta */}
            <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#0f172a] via-[#1e293b] to-[#0f172a] p-8 shadow-xl">
                <div className="absolute top-0 right-0 w-64 h-64 bg-[#7dd3fc]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" />
                <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#38bdf8]/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/4" />
                <div className="relative flex items-center gap-4">
                    <div className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm border border-white/10">
                        <svg className="w-6 h-6 text-[#7dd3fc]" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-white">Configurações Meta</h1>
                        <p className="mt-1 text-sm text-slate-300">Gerencie páginas, categorias e contas do Meta Ads</p>
                    </div>
                </div>
            </div>

            {/* Content: Sidebar + Main */}
            <div className="flex gap-6">
                {/* Vertical Sidebar Menu */}
                <div className="w-52 flex-shrink-0 flex flex-col gap-1">
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider px-3 mb-2">Menu</p>
                    {([
                        { id: 'pages', label: 'Páginas' },
                        { id: 'page-groups', label: 'Grupos de Páginas' },
                        { id: 'categories', label: 'Categorias' },
                        { id: 'apps', label: 'Apps' },
                        { id: 'accounts', label: 'Contas e Usuários' },
                        { id: 'collaborators', label: 'Colaboradores' },
                    ] as { id: MetaConnectionTab; label: string }[]).map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setActiveTab(item.id)}
                            className={`w-full text-left px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 ${activeTab === item.id
                                ? 'bg-blue-50 text-blue-700 border border-blue-100'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                                }`}
                        >
                            {item.label}
                        </button>
                    ))}
                </div>

                {/* Main Content */}
                <div className="flex-1 min-w-0">
                    {isLoading && (
                        <div className="rounded-2xl bg-white p-8 shadow-sm border border-gray-100">
                            <div className="flex items-center justify-center">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#0f172a]"></div>
                                <p className="ml-3 text-gray-500">Carregando configurações...</p>
                            </div>
                        </div>
                    )}

                    {!isLoading && (
                        <div className="animate-in fade-in slide-in-from-bottom-2 duration-300">
                            {activeTab === 'pages' && (
                                <PagesList
                                    pages={pages}
                                    onToggleStatus={handleTogglePageStatus}
                                    onConfigure={handleOpenConfigurePage}
                                    onSyncLeads={handleOpenSyncLeads}
                                    onConnect={handleOpenConnectDialog}
                                    onDeletePage={handleDeletePage}
                                />
                            )}
                            {activeTab === 'page-groups' && (
                                <PageGroupsList
                                    groups={pageGroups}
                                    onCreate={handleOpenCreatePageGroup}
                                    onEdit={handleOpenEditPageGroup}
                                    onDelete={setPageGroupToDelete}
                                />
                            )}
                            {activeTab === 'categories' && <CategoriesList />}
                            {activeTab === 'apps' && (
                                <AppsList
                                    apps={apps.map(a => ({
                                        ...a,
                                        // Enriquece com project_name caso backend não retorne (fallback do mapa)
                                        project_name: a.project_name ?? (a.project_id ? (projectNameById.get(a.project_id) ?? null) : null),
                                    }))}
                                    onCreateApp={() => {
                                        // Non-admin só cria app vinculado a projeto.
                                        // Força is_private=true ao abrir.
                                        if (!isSuperAdmin) {
                                            setAppFormData((prev) => ({ ...prev, is_private: true }));
                                        }
                                        setIsCreateAppDialogOpen(true);
                                    }}
                                    onEditApp={handleOpenEditAppDialog}
                                    onEditAppProjects={handleOpenEditAppProjects}
                                    onEditRateLimit={handleOpenRateLimitDialog}
                                    onDeleteApp={handleOpenDeleteAppDialog}
                                    onToggleAppStatus={handleToggleAppStatus}
                                    onViewWebhook={handleOpenWebhookConfig}
                                    canManageApp={canManageApp}
                                    canCreateApp={isSuperAdmin || userProjectIds.size > 0}
                                />
                            )}
                            {activeTab === 'accounts' && (
                                <AccountsList
                                    authentications={authentications}
                                    pages={pages}
                                    projects={projectOptions}
                                    onSync={handleSyncPages}
                                    onRevoke={handleRevokeAuth}
                                    onConnectAccount={handleOpenConnectDialog}
                                    onUpdateAuthProject={(authId, projectId) =>
                                        updateAuthProjectMutation.mutate({ authId, projectId })
                                    }
                                    onUpdatePageProject={(pageId, projectId) =>
                                        updatePageProjectMutation.mutate({ pageId, projectId })
                                    }
                                />
                            )}
                            {activeTab === 'collaborators' && (
                                <CollaboratorsList pages={pages} />
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Dialogs */}
            <CreateEditAppDialog
                open={isCreateAppDialogOpen}
                onOpenChange={(open) => { if (!open) handleCloseCreateAppDialog(); else setIsCreateAppDialogOpen(true); }}
                formData={appFormData}
                onFormChange={setAppFormData}
                onSubmit={handleCreateAppSubmit}
                isLoading={createAppMutation.isPending}
                availableProjects={projectOptions}
                canCreatePublic={isSuperAdmin}
            />
            <CreateEditAppDialog
                open={isEditAppDialogOpen}
                onOpenChange={(open) => { if (!open) handleCloseEditAppDialog(); else setIsEditAppDialogOpen(true); }}
                formData={appFormData}
                onFormChange={setAppFormData}
                onSubmit={handleEditAppSubmit}
                isLoading={updateAppMutation.isPending}
                isEdit
                availableProjects={projectOptions}
            />
            <EditAppProjectsDialog
                open={editAppProjectsTarget !== null}
                onOpenChange={(open) => { if (!open) setEditAppProjectsTarget(null); }}
                app={editAppProjectsTarget}
                availableProjects={projectOptions}
                onSubmit={handleSaveAppProjects}
                saving={updateAppMutation.isPending}
            />
            <EditRateLimitDialog
                open={isRateLimitDialogOpen}
                onOpenChange={(open) => {
                    setIsRateLimitDialogOpen(open);
                    if (!open) setRateLimitApp(null);
                }}
                app={rateLimitApp}
                onSubmit={handleSaveRateLimit}
                isLoading={updateAppMutation.isPending}
            />
            <ConnectAccountDialog
                open={isConnectDialogOpen}
                onOpenChange={setIsConnectDialogOpen}
                apps={apps}
                selectedAppId={selectedAppIdForConnect}
                onAppSelect={setSelectedAppIdForConnect}
                onConnect={handleConnect}
                isLoading={initOAuthMutation.isPending}
            />
            <DeleteAppDialog
                open={isDeleteAppDialogOpen}
                onOpenChange={(open) => { if (!open) handleCloseDeleteAppDialog(); else setIsDeleteAppDialogOpen(true); }}
                app={appToDelete}
                onConfirm={handleConfirmDeleteApp}
                isLoading={deleteAppMutation.isPending}
            />
            <WebhookConfigDialog
                open={isWebhookConfigDialogOpen}
                onOpenChange={(open) => {
                    setIsWebhookConfigDialogOpen(open);
                    if (!open) setWebhookConfigApp(null);
                }}
                app={webhookConfigApp}
            />
            <ConfigurePageDialog
                open={isConfigurePageDialogOpen}
                onOpenChange={setIsConfigurePageDialogOpen}
                page={pageToConfigure}
                categories={categories}
                onSave={handleSavePageConfig}
                isLoading={updatePageConfigMutation.isPending}
            />
            <SyncLeadsDialog
                open={isSyncLeadsDialogOpen}
                onOpenChange={setIsSyncLeadsDialogOpen}
                pages={pages}
                onSyncComplete={handleSyncLeadsComplete}
                initialSelectedPageId={pageToSync?.id}
                onBackgroundSync={handleBackgroundSync}
            />

            {activeSyncTaskId && (
                <FloatingSyncStatus
                    taskId={activeSyncTaskId}
                    onClose={() => setActiveSyncTaskId(null)}
                />
            )}

            <SyncPagesDialog
                auth={syncAuth}
                onClose={() => setSyncAuth(null)}
            />

            <ConfirmDialog
                open={!!authToRevoke}
                onOpenChange={(open) => { if (!open) setAuthToRevoke(null); }}
                variant="danger"
                title="Excluir conta conectada"
                description={`Tem certeza que deseja excluir a conta "${authToRevoke?.meta_user_name}"? Todas as páginas vinculadas a esta conta também serão excluídas permanentemente. Esta ação não pode ser desfeita.`}
                confirmLabel="Excluir"
                cancelLabel="Cancelar"
                isLoading={revokeAuthMutation.isPending}
                onConfirm={handleConfirmRevoke}
            />
            <ConfirmDialog
                open={!!pageToDelete}
                onOpenChange={(open) => { if (!open) setPageToDelete(null); }}
                variant="danger"
                title="Remover página"
                description={`Tem certeza que deseja remover a página "${pageToDelete?.page_name}"? Esta ação não pode ser desfeita.`}
                confirmLabel="Remover"
                cancelLabel="Cancelar"
                isLoading={deletePageMutation.isPending}
                onConfirm={handleConfirmDeletePage}
            />

            <CreateEditPageGroupDialog
                open={isPageGroupDialogOpen}
                onOpenChange={(open) => {
                    setIsPageGroupDialogOpen(open);
                    if (!open) setEditingPageGroup(null);
                }}
                apps={apps.map(a => ({
                    ...a,
                    // Enriquece com project_name a partir do mapa local (backend
                    // não resolve ainda — fallback necessário pra dropdown bonito).
                    project_name: a.project_name ?? (a.project_id ? (projectNameById.get(a.project_id) ?? null) : null),
                }))}
                pages={pages}
                existingGroups={pageGroups}
                editingGroup={editingPageGroup}
                onSubmit={handleSubmitPageGroup}
                isLoading={createPageGroupMutation.isPending || updatePageGroupMutation.isPending}
            />

            <ConfirmDialog
                open={!!pageGroupToDelete}
                onOpenChange={(open) => { if (!open) setPageGroupToDelete(null); }}
                variant="danger"
                title="Excluir grupo de páginas"
                description={`Excluir o grupo "${pageGroupToDelete?.name}"? As páginas voltam a usar o primeiro app conectado. Esta ação não pode ser desfeita.`}
                confirmLabel="Excluir grupo"
                cancelLabel="Cancelar"
                isLoading={deletePageGroupMutation.isPending}
                onConfirm={() => pageGroupToDelete && deletePageGroupMutation.mutate(pageGroupToDelete.id)}
            />
        </div>
    );
}
